import React, { useState, useEffect, useCallback } from 'react';
import './FilterSettings.css';

function FilterSettings({ token, forcedSubTab, onSubTabChange }) {
  const [activeSubTab, setActiveSubTabState] = useState('categories');

  // Гид по онбордингу управляет вкладкой снаружи (forcedSubTab) — пользователь
  // при этом всё равно может кликать по вкладкам сам, это не блокируется.
  useEffect(() => {
    if (forcedSubTab) setActiveSubTabState(forcedSubTab);
  }, [forcedSubTab]);

  const setActiveSubTab = (tab) => {
    setActiveSubTabState(tab);
    if (onSubTabChange) onSubTabChange(tab);
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Data
  const [categories, setCategories] = useState([]);
  const [features, setFeatures] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [patternRules, setPatternRules] = useState([]);
  const [history, setHistory] = useState([]);

  // Fine-tuning
  const [fineTuneInfo, setFineTuneInfo] = useState(null);
  const [fineTuneLoading, setFineTuneLoading] = useState(false);
  const [fineTuneError, setFineTuneError] = useState('');
  const [trainingExamples, setTrainingExamples] = useState(null); // null = not loaded
  const [examplesLoading, setExamplesLoading] = useState(false);


  // Enrichment rules
  const [enrichmentRules, setEnrichmentRules] = useState([]);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [showNewEnrichment, setShowNewEnrichment] = useState(false);
  const [newEnrichment, setNewEnrichment] = useState({ trigger_synonyms_text: '', add_value: '', apply_to_categories: ['all'], description: '', is_active: true });
  const [editingEnrichment, setEditingEnrichment] = useState(null);

  // Edit modals
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);

  // New item forms
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewFeature, setShowNewFeature] = useState(false);

  // Массовое включение характеристик (по умолчанию выключены после генерации при онбординге)
  const [selectedFeatureIds, setSelectedFeatureIds] = useState(new Set());
  const [bulkActivating, setBulkActivating] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Fetch data
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchPrompts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPrompts(data);
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchPatternRules = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/pattern-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPatternRules(data);
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchFineTuneInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/fine-tune`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setFineTuneInfo(await response.json());
      }
    } catch (err) {
      // ignore
    }
  }, [API_URL, token]);

  const fetchEnrichmentRules = useCallback(async () => {
    setEnrichmentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/enrichment-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setEnrichmentRules(await response.json());
      }
    } catch (err) {
      // ignore
    } finally {
      setEnrichmentLoading(false);
    }
  }, [API_URL, token]);

  const handleSaveEnrichment = async (data, id = null) => {
    const url = id
      ? `${API_URL}/api/settings/enrichment-rules/${id}`
      : `${API_URL}/api/settings/enrichment-rules`;
    const method = id ? 'PUT' : 'POST';
    const payload = {
      ...data,
      trigger_synonyms: data.trigger_synonyms_text
        ? data.trigger_synonyms_text.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
        : (data.trigger_synonyms || []),
    };
    delete payload.trigger_synonyms_text;
    try {
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setSuccessMessage(id ? 'Правило обновлено. Номенклатура пересканирована.' : 'Правило добавлено. Номенклатура пересканирована.');
        setTimeout(() => setSuccessMessage(''), 4000);
        setShowNewEnrichment(false);
        setEditingEnrichment(null);
        setNewEnrichment({ trigger_synonyms_text: '', add_value: '', apply_to_categories: ['all'], description: '', is_active: true });
        fetchEnrichmentRules();
      }
    } catch (err) {
      setError('Ошибка сохранения правила');
    }
  };

  const handleDeleteEnrichment = async (id) => {
    if (!window.confirm('Удалить правило? Номенклатура будет пересканирована.')) return;
    try {
      await fetch(`${API_URL}/api/settings/enrichment-rules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchEnrichmentRules();
    } catch (err) {
      setError('Ошибка удаления');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchFeatures(), fetchPrompts(), fetchPatternRules(), fetchHistory(), fetchFineTuneInfo()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchFeatures, fetchPrompts, fetchPatternRules, fetchHistory, fetchFineTuneInfo]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 5000);
  };

  // ============================================
  // CATEGORIES
  // ============================================
  const handleSaveCategory = async (category) => {
    setSaving(true);
    try {
      const isNew = !category.id;
      const url = isNew
        ? `${API_URL}/api/settings/categories`
        : `${API_URL}/api/settings/categories/${category.id}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(category),
      });

      if (response.ok) {
        showSuccess(isNew ? 'Категория добавлена' : 'Категория обновлена');
        await fetchCategories();
        await fetchHistory();
        setEditingCategory(null);
        setShowNewCategory(false);
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка сохранения');
      }
    } catch (err) {
      showError('Ошибка сохранения категории');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Удалить категорию?')) return;

    try {
      const response = await fetch(`${API_URL}/api/settings/categories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        showSuccess('Категория удалена');
        await fetchCategories();
        await fetchHistory();
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка удаления');
      }
    } catch (err) {
      showError('Ошибка удаления категории');
    }
  };

  // ============================================
  // FEATURES
  // ============================================
  const handleSaveFeature = async (feature) => {
    setSaving(true);
    try {
      const isNew = !feature.id;
      const url = isNew
        ? `${API_URL}/api/settings/features`
        : `${API_URL}/api/settings/features/${feature.id}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feature),
      });

      if (response.ok) {
        showSuccess(isNew ? 'Характеристика добавлена' : 'Характеристика обновлена');
        await fetchFeatures();
        await fetchHistory();
        setEditingFeature(null);
        setShowNewFeature(false);
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка сохранения');
      }
    } catch (err) {
      showError('Ошибка сохранения характеристики');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (id) => {
    if (!window.confirm('Удалить характеристику?')) return;

    try {
      const response = await fetch(`${API_URL}/api/settings/features/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        showSuccess('Характеристика удалена');
        await fetchFeatures();
        await fetchHistory();
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка удаления');
      }
    } catch (err) {
      showError('Ошибка удаления характеристики');
    }
  };

  const toggleFeatureSelected = (id) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllFeatures = () => {
    setSelectedFeatureIds(new Set(features.map((f) => f.id)));
  };

  const clearFeatureSelection = () => setSelectedFeatureIds(new Set());

  const handleBulkActivateFeatures = async (isActive) => {
    if (selectedFeatureIds.size === 0) return;
    setBulkActivating(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/features/bulk-active`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feature_ids: Array.from(selectedFeatureIds), is_active: isActive }),
      });
      if (response.ok) {
        const data = await response.json();
        showSuccess(`${isActive ? 'Включено' : 'Выключено'} характеристик: ${data.updated}`);
        clearFeatureSelection();
        await fetchFeatures();
        await fetchHistory();
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка массового обновления');
      }
    } catch (err) {
      showError('Ошибка массового обновления характеристик');
    } finally {
      setBulkActivating(false);
    }
  };

  // ============================================
  // PROMPTS
  // ============================================
  const handleSavePrompt = async (prompt) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/prompts/${prompt.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: prompt.content }),
      });

      if (response.ok) {
        showSuccess('Промпт обновлен');
        await fetchPrompts();
        await fetchHistory();
        setEditingPrompt(null);
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка сохранения');
      }
    } catch (err) {
      showError('Ошибка сохранения промпта');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // HISTORY & ROLLBACK
  // ============================================
  const handleRollback = async (historyId) => {
    if (!window.confirm('Откатить к этой версии? Текущие значения будут заменены.')) return;

    try {
      const response = await fetch(`${API_URL}/api/settings/history/${historyId}/rollback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        showSuccess('Откат выполнен успешно');
        await Promise.all([fetchCategories(), fetchFeatures(), fetchPrompts(), fetchHistory()]);
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка отката');
      }
    } catch (err) {
      showError('Ошибка отката');
    }
  };

  // ============================================
  // PATTERN RULES
  // ============================================
  const handleTogglePatternRule = async (rule) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/pattern-rules/${rule.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: !rule.is_active,
          apply_to_categories: rule.apply_to_categories || [],
        }),
      });

      if (response.ok) {
        showSuccess(rule.is_active ? `Правило "${rule.name}" отключено` : `Правило "${rule.name}" включено`);
        await fetchPatternRules();
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка сохранения');
      }
    } catch (err) {
      showError('Ошибка обновления правила');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePatternRuleCategories = async (rule, newCategories) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/pattern-rules/${rule.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: rule.is_active,
          apply_to_categories: newCategories,
        }),
      });

      if (response.ok) {
        showSuccess('Категории обновлены');
        await fetchPatternRules();
      } else {
        const err = await response.json();
        showError(err.detail || 'Ошибка сохранения');
      }
    } catch (err) {
      showError('Ошибка обновления правила');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // RELOAD CACHE
  // ============================================
  const handleReloadCache = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/reload-cache`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        showSuccess('Кеш фильтров перезагружен. Пересчёт номенклатуры (характеристики, обогащение, группы, исключения) запущен в фоне — может занять несколько минут.');
      } else {
        showError('Ошибка перезагрузки кеша');
      }
    } catch (err) {
      showError('Ошибка перезагрузки кеша');
    }
  };

  // ============================================
  // EXPORT MIGRATION
  // ============================================
  const handleExportMigration = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/export-migration`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        // Получаем имя файла из заголовка
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'migration_export.py';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1];
        }

        // Скачиваем файл
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        showSuccess('Файл миграции скачан');
      } else {
        showError('Ошибка экспорта');
      }
    } catch (err) {
      showError('Ошибка экспорта миграции');
    }
  };

  const handleImportMigration = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/settings/import-migration`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        const s = data.stats || {};
        showSuccess(`Миграция загружена: ${s.categories ?? 0} категорий, ${s.features ?? 0} характеристик, ${s.prompts ?? 0} промптов, ${s.enrichment_rules ?? 0} правил обогащения. Номенклатура пересканируется...`);
        fetchCategories();
        fetchFeatures();
        fetchPrompts();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(`Ошибка импорта: ${err.detail || response.statusText}`);
      }
    } catch (err) {
      showError('Ошибка загрузки файла миграции');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">Загрузка настроек фильтров...</div>;
  }

  return (
    <div className="filter-settings">
      <div className="filter-settings-header">
        <h2>Настройки фильтрации</h2>
        <div className="header-buttons">
          <button className="export-btn" onClick={handleExportMigration}>
            Выгрузить в файл миграции
          </button>
          <label className="export-btn" style={{ cursor: 'pointer', marginLeft: '8px' }}>
            Загрузить файл миграции
            <input
              type="file"
              accept=".py"
              style={{ display: 'none' }}
              onChange={handleImportMigration}
            />
          </label>
          <button className="reload-cache-btn" onClick={handleReloadCache}>
            Перезагрузить кеш
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Sub-tabs */}
      <div className="filter-subtabs">
        <button
          className={`subtab ${activeSubTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('categories')}
        >
          Категории ({categories.length})
        </button>
        <button
          className={`subtab ${activeSubTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('features')}
        >
          Характеристики ({features.length})
        </button>
        <button
          className={`subtab ${activeSubTab === 'pattern-rules' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('pattern-rules')}
        >
          Правила сопоставления ({patternRules.length})
        </button>
        <button
          className={`subtab ${activeSubTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('prompts')}
        >
          Промпты GPT ({prompts.length})
        </button>
        <button
          className={`subtab ${activeSubTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('history')}
        >
          История
        </button>
        <button
          className={`subtab ${activeSubTab === 'training' ? 'active' : ''}`}
          onClick={() => { setActiveSubTab('training'); fetchFineTuneInfo(); }}
        >
          Обучение {fineTuneInfo ? `(${fineTuneInfo.training_count})` : ''}
        </button>
      </div>

      {/* Categories Tab */}
      {activeSubTab === 'categories' && (
        <div className="subtab-content">
          <div className="subtab-header">
            <p>Категории товаров (сверло, метчик, плашка и т.д.) с синонимами для распознавания</p>
            <button className="add-btn" onClick={() => setShowNewCategory(true)}>
              + Добавить категорию
            </button>
          </div>

          <div className="items-list">
            {categories.map((cat) => (
              <div key={cat.id} className={`item-card ${!cat.is_active ? 'inactive' : ''}`}>
                <div className="item-main">
                  <div className="item-name">{cat.name}</div>
                  <div className="item-synonyms">
                    {cat.synonyms && cat.synonyms.length > 0
                      ? cat.synonyms.join(', ')
                      : <span className="no-synonyms">нет синонимов</span>}
                  </div>
                </div>
                <div className="item-meta">
                  <span className={`status-badge ${cat.is_active ? 'active' : 'inactive'}`}>
                    {cat.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                  <span className="sort-order">#{cat.sort_order}</span>
                </div>
                <div className="item-actions">
                  <button className="edit-btn" onClick={() => setEditingCategory(cat)}>
                    Изменить
                  </button>
                  <button className="delete-btn" onClick={() => handleDeleteCategory(cat.id)}>
                    Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features Tab */}
      {activeSubTab === 'features' && (
        <div className="subtab-content">
          <div className="subtab-header">
            <p>Характеристики для фильтрации (марки стали, направление, типы и т.д.). Новые характеристики
              по умолчанию выключены — включайте только те, без которых позицию нельзя однозначно подобрать.</p>
            <button className="add-btn" onClick={() => setShowNewFeature(true)}>
              + Добавить характеристику
            </button>
          </div>

          <div className="bulk-actions-bar">
            <button type="button" className="secondary-btn" onClick={selectAllFeatures} disabled={features.length === 0}>
              Выбрать все ({features.length})
            </button>
            <button type="button" className="secondary-btn" onClick={clearFeatureSelection} disabled={selectedFeatureIds.size === 0}>
              Снять выбор
            </button>
            <span className="bulk-selected-count">
              {selectedFeatureIds.size > 0 ? `Выбрано: ${selectedFeatureIds.size}` : ''}
            </span>
            <button
              type="button"
              className="save-btn"
              disabled={selectedFeatureIds.size === 0 || bulkActivating}
              onClick={() => handleBulkActivateFeatures(true)}
            >
              {bulkActivating ? 'Включение...' : 'Включить выбранные'}
            </button>
            <button
              type="button"
              className="delete-btn"
              disabled={selectedFeatureIds.size === 0 || bulkActivating}
              onClick={() => handleBulkActivateFeatures(false)}
            >
              Выключить выбранные
            </button>
          </div>

          <div className="items-list">
            {features.map((feat) => (
              <div key={feat.id} className={`item-card ${!feat.is_active ? 'inactive' : ''}`}>
                <div className="item-select">
                  <input
                    type="checkbox"
                    checked={selectedFeatureIds.has(feat.id)}
                    onChange={() => toggleFeatureSelected(feat.id)}
                  />
                </div>
                <div className="item-main">
                  <div className="item-name">
                    {feat.name}
                    {feat.is_asymmetric && <span className="asymmetric-badge">асимм.</span>}
                    {feat.use_as_fallback && <span className="fallback-badge">резерв</span>}
                    {feat.is_steel_grade && <span className="steel-grade-badge">марка стали</span>}
                    {feat.is_thread_type && <span className="thread-type-badge">тип резьбы</span>}
                  </div>
                  <div className="item-synonyms">
                    {feat.synonyms && feat.synonyms.length > 0
                      ? feat.synonyms.join(', ')
                      : <span className="no-synonyms">нет синонимов</span>}
                  </div>
                  {feat.description && (
                    <div className="item-description">{feat.description}</div>
                  )}
                  <div className="item-categories">
                    <strong>Применяется к: </strong>
                    {feat.apply_to_categories && feat.apply_to_categories.length > 0
                      ? feat.apply_to_categories.join(', ')
                      : 'все категории'}
                  </div>
                </div>
                <div className="item-meta">
                  <span className={`status-badge ${feat.is_active ? 'active' : 'inactive'}`}>
                    {feat.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                  <span className="sort-order">#{feat.sort_order}</span>
                </div>
                <div className="item-actions">
                  <button className="edit-btn" onClick={() => setEditingFeature(feat)}>
                    Изменить
                  </button>
                  <button className="delete-btn" onClick={() => handleDeleteFeature(feat.id)}>
                    Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pattern Rules Tab */}
      {activeSubTab === 'pattern-rules' && (
        <div className="subtab-content">
          <div className="subtab-header">
            <p>Правила проверки обозначений и характеристик. Можно включать/выключать и назначать категории.</p>
          </div>

          <div className="items-list">
            {patternRules.length === 0 ? (
              <div className="no-history">Правила не найдены. Запустите миграцию: migrations/add_pattern_filters.py</div>
            ) : (
              patternRules.map((rule) => (
                <PatternRuleCard
                  key={rule.id}
                  rule={rule}
                  categories={categories}
                  saving={saving}
                  onToggle={handleTogglePatternRule}
                  onSaveCategories={handleSavePatternRuleCategories}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Prompts Tab */}
      {activeSubTab === 'prompts' && (
        <div className="subtab-content">
          <div className="subtab-header">
            <p>Промпты для GPT (парсинг заказов, выбор номенклатуры)</p>
          </div>

          <div className="prompts-list">
            {[...prompts]
              .sort((a, b) => {
                const order = ['order_parsing', 'order_parsing_specifics', 'order_matching', 'order_matching_specifics'];
                return order.indexOf(a.name) - order.indexOf(b.name);
              })
              .map((prompt) => {
                const isSpecifics = prompt.name.endsWith('_specifics');
                const isEmpty = !prompt.content || !prompt.content.trim();
                return (
                  <div key={prompt.id} className={`prompt-card${isSpecifics ? ' prompt-card-specifics' : ''}`}>
                    <div className="prompt-header">
                      <div className="prompt-name">
                        {prompt.display_name || prompt.name}
                        {isSpecifics && <span className="prompt-specifics-badge">специфика</span>}
                      </div>
                      <div className="prompt-meta">
                        Обновлен: {formatDate(prompt.updated_at)}
                      </div>
                    </div>
                    {prompt.description && (
                      <div className="prompt-description">{prompt.description}</div>
                    )}
                    <div className="prompt-content">
                      {isEmpty ? (
                        <div className="prompt-empty-state">
                          Пока не заполнено — эта часть не отправляется в GPT.
                        </div>
                      ) : (
                        <pre>{prompt.content.substring(0, 500)}{prompt.content.length > 500 ? '...' : ''}</pre>
                      )}
                    </div>
                    <div className="prompt-actions">
                      <button className="edit-btn" onClick={() => setEditingPrompt(prompt)}>
                        {isSpecifics && isEmpty ? 'Заполнить' : 'Редактировать'}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeSubTab === 'history' && (
        <div className="subtab-content">
          <div className="subtab-header">
            <p>История изменений настроек с возможностью отката</p>
            <button className="refresh-btn" onClick={fetchHistory}>
              Обновить
            </button>
          </div>

          <div className="history-list">
            {history.length === 0 ? (
              <div className="no-history">История изменений пуста</div>
            ) : (
              history.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-main">
                    <div className="history-action">
                      <span className={`action-badge ${item.action}`}>
                        {item.action === 'create' ? 'Создание' :
                         item.action === 'update' ? 'Изменение' :
                         item.action === 'delete' ? 'Удаление' : item.action}
                      </span>
                      <span className="entity-type">
                        {item.entity_type === 'category' ? 'Категория' :
                         item.entity_type === 'feature' ? 'Характеристика' :
                         item.entity_type === 'prompt' ? 'Промпт' : item.entity_type}
                      </span>
                      <span className="entity-name">{item.entity_name}</span>
                    </div>
                    <div className="history-date">{formatDate(item.created_at)}</div>
                  </div>
                  {item.old_value && (
                    <div className="history-values">
                      <div className="old-value">
                        <strong>Было:</strong>
                        <pre>{typeof item.old_value === 'object'
                          ? JSON.stringify(item.old_value, null, 2)
                          : item.old_value}</pre>
                      </div>
                    </div>
                  )}
                  <div className="history-actions">
                    {item.old_value && (
                      <button
                        className="rollback-btn"
                        onClick={() => handleRollback(item.id)}
                      >
                        Откатить
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Training Tab */}
      {activeSubTab === 'training' && (
        <div className="subtab-content">
          <div className="subtab-header" style={{ marginTop: 32 }}>
            <h3>Обучение модели</h3>
            <p className="subtab-description">
              Дообучение GPT-5.4 на основе вручную подтверждённых сопоставлений.
              Обученная модель используется вместо базовой при финальном выборе кандидата.
            </p>
          </div>

          {fineTuneError && <div className="error-message">{fineTuneError}</div>}

          {fineTuneLoading && !fineTuneInfo ? (
            <div className="loading">Загрузка...</div>
          ) : fineTuneInfo && (
            <div className="training-panel">
              {/* Stats */}
              <div className="training-stats">
                <div className="training-stat-card">
                  <div className="training-stat-value">{fineTuneInfo.training_count}</div>
                  <div className="training-stat-label">примеров для обучения</div>
                </div>
                {fineTuneInfo.model_id && (
                  <div className="training-stat-card">
                    <div className="training-stat-value" style={{fontSize: '0.75rem', wordBreak: 'break-all'}}>{fineTuneInfo.model_id}</div>
                    <div className="training-stat-label">текущая обученная модель</div>
                  </div>
                )}
              </div>

              {/* Job status */}
              {fineTuneInfo.job_id && (
                <div className={`training-job-status status-${fineTuneInfo.job_status}`}>
                  <strong>Задача обучения:</strong> {fineTuneInfo.job_id}
                  <span className="job-status-badge">{fineTuneInfo.job_status}</span>
                  {['running', 'queued', 'validating_files'].includes(fineTuneInfo.job_status) && (
                    <button
                      className="check-status-btn"
                      onClick={async () => {
                        setFineTuneLoading(true);
                        try {
                          const r = await fetch(`${API_URL}/api/settings/fine-tune/status`, {headers: {Authorization: `Bearer ${token}`}});
                          if (r.ok) setFineTuneInfo(await r.json());
                        } catch(e) { setFineTuneError('Ошибка проверки статуса'); }
                        setFineTuneLoading(false);
                      }}
                      disabled={fineTuneLoading}
                    >
                      Обновить статус
                    </button>
                  )}
                </div>
              )}

              {/* Use fine-tuned model toggle */}
              {fineTuneInfo.model_id && (
                <div className="training-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={fineTuneInfo.use_finetuned === true}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        try {
                          const r = await fetch(`${API_URL}/api/settings/fine-tune/settings`, {
                            method: 'PATCH',
                            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
                            body: JSON.stringify({use_finetuned: val})
                          });
                          if (r.ok) setFineTuneInfo(prev => ({...prev, use_finetuned: val}));
                          else setFineTuneError('Ошибка сохранения настройки');
                        } catch(e) { setFineTuneError('Ошибка сохранения настройки'); }
                      }}
                    />
                    Использовать обученную модель при сопоставлении
                  </label>
                </div>
              )}

              {/* Start training button */}
              <div className="training-actions">
                <button
                  className="start-training-btn"
                  disabled={fineTuneLoading || fineTuneInfo.training_count === 0 || ['running', 'queued', 'validating_files'].includes(fineTuneInfo.job_status)}
                  onClick={async () => {
                    if (!window.confirm(`Запустить обучение на ${fineTuneInfo.training_count} примерах?`)) return;
                    setFineTuneLoading(true);
                    setFineTuneError('');
                    try {
                      const r = await fetch(`${API_URL}/api/settings/fine-tune/start`, {method: 'POST', headers: {Authorization: `Bearer ${token}`}});
                      const data = await r.json();
                      if (r.ok) {
                        setFineTuneInfo(prev => ({...prev, job_id: data.job_id, job_status: 'queued'}));
                      } else {
                        setFineTuneError(data.detail || 'Ошибка запуска обучения');
                      }
                    } catch(e) { setFineTuneError('Ошибка запуска обучения'); }
                    setFineTuneLoading(false);
                  }}
                >
                  {fineTuneInfo.model_id ? 'Дообучить модель' : 'Начать обучение'}
                  {fineTuneLoading && ' ...'}
                </button>
                {fineTuneInfo.training_count === 0 && (
                  <p className="training-hint">Нет примеров для обучения. Отметьте позиции «В обучении» на странице результатов.</p>
                )}
              </div>

              {/* Examples list */}
              {fineTuneInfo.training_count > 0 && (
                <div className="training-examples-section">
                  <div className="training-examples-header">
                    <h4>Примеры для обучения ({fineTuneInfo.training_count})</h4>
                    {trainingExamples === null ? (
                      <button
                        className="show-examples-btn"
                        disabled={examplesLoading}
                        onClick={async () => {
                          setExamplesLoading(true);
                          try {
                            const r = await fetch(`${API_URL}/api/settings/fine-tune/examples?limit=200`, {headers: {Authorization: `Bearer ${token}`}});
                            if (r.ok) setTrainingExamples((await r.json()).examples);
                          } catch(e) { setFineTuneError('Ошибка загрузки примеров'); }
                          setExamplesLoading(false);
                        }}
                      >
                        {examplesLoading ? 'Загрузка...' : 'Показать примеры'}
                      </button>
                    ) : (
                      <button className="show-examples-btn" onClick={() => setTrainingExamples(null)}>Скрыть</button>
                    )}
                  </div>
                  {trainingExamples && (
                    <div className="training-examples-list">
                      <div className="training-examples-row training-examples-head">
                        <span>Заказ</span>
                        <span>Сопоставлено с</span>
                        <span></span>
                      </div>
                      {trainingExamples.map(ex => (
                        <div key={ex.id} className="training-examples-row">
                          <span className="ex-order">{ex.order_name}</span>
                          <span className="ex-matched">{ex.matched_name}</span>
                          <button
                            className="ex-delete-btn"
                            title="Удалить из обучения"
                            onClick={async () => {
                              if (!window.confirm('Удалить этот пример?')) return;
                              try {
                                const r = await fetch(`${API_URL}/api/settings/fine-tune/examples/${ex.id}`, {
                                  method: 'DELETE', headers: {Authorization: `Bearer ${token}`}
                                });
                                if (r.ok) {
                                  setTrainingExamples(prev => prev.filter(e => e.id !== ex.id));
                                  setFineTuneInfo(prev => ({...prev, training_count: prev.training_count - 1}));
                                }
                              } catch(e) { setFineTuneError('Ошибка удаления'); }
                            }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Enrichment Rules Tab - moved to Settings → Nomenclature */}
      {false && (
        <div className="tab-content">
          <div className="tab-header">
          </div>

          {enrichmentLoading && <div className="loading-text">Загрузка...</div>}

          {showNewEnrichment && (
            <div className="enrichment-form card">
              <h4>Новое правило</h4>
              <div className="form-row">
                <label>Признак (синонимы через запятую)</label>
                <input
                  type="text"
                  placeholder="м-р, мр, м/р"
                  value={newEnrichment.trigger_synonyms_text}
                  onChange={e => setNewEnrichment({...newEnrichment, trigger_synonyms_text: e.target.value})}
                />
              </div>
              <div className="form-row">
                <label>Добавить значение</label>
                <input
                  type="text"
                  placeholder="9хс"
                  value={newEnrichment.add_value}
                  onChange={e => setNewEnrichment({...newEnrichment, add_value: e.target.value})}
                />
              </div>
              <div className="form-row">
                <label>Описание (необязательно)</label>
                <input
                  type="text"
                  placeholder="Метчики м-р всегда из 9ХС"
                  value={newEnrichment.description}
                  onChange={e => setNewEnrichment({...newEnrichment, description: e.target.value})}
                />
              </div>
              <div className="form-actions">
                <button className="save-btn" onClick={() => handleSaveEnrichment(newEnrichment)} disabled={!newEnrichment.trigger_synonyms_text || !newEnrichment.add_value}>
                  Сохранить
                </button>
                <button className="cancel-btn" onClick={() => { setShowNewEnrichment(false); setNewEnrichment({ trigger_synonyms_text: '', add_value: '', apply_to_categories: ['all'], description: '', is_active: true }); }}>
                  Отмена
                </button>
              </div>
            </div>
          )}

          {enrichmentRules.length === 0 && !enrichmentLoading && !showNewEnrichment && (
            <div className="empty-state">Правил пока нет. Нажмите «+ Добавить правило».</div>
          )}

          <div className="enrichment-list">
            {enrichmentRules.map(rule => (
              <div key={rule.id} className={`enrichment-item card ${!rule.is_active ? 'inactive' : ''}`}>
                {editingEnrichment?.id === rule.id ? (
                  <>
                    <div className="form-row">
                      <label>Признак (синонимы через запятую)</label>
                      <input
                        type="text"
                        value={editingEnrichment.trigger_synonyms_text}
                        onChange={e => setEditingEnrichment({...editingEnrichment, trigger_synonyms_text: e.target.value})}
                      />
                    </div>
                    <div className="form-row">
                      <label>Добавить значение</label>
                      <input
                        type="text"
                        value={editingEnrichment.add_value}
                        onChange={e => setEditingEnrichment({...editingEnrichment, add_value: e.target.value})}
                      />
                    </div>
                    <div className="form-row">
                      <label>Описание</label>
                      <input
                        type="text"
                        value={editingEnrichment.description || ''}
                        onChange={e => setEditingEnrichment({...editingEnrichment, description: e.target.value})}
                      />
                    </div>
                    <div className="form-actions">
                      <button className="save-btn" onClick={() => handleSaveEnrichment(editingEnrichment, rule.id)}>Сохранить</button>
                      <button className="cancel-btn" onClick={() => setEditingEnrichment(null)}>Отмена</button>
                    </div>
                  </>
                ) : (
                  <div className="enrichment-item-content">
                    <div className="enrichment-triggers">
                      {(rule.trigger_synonyms || []).map(t => (
                        <span key={t} className="synonym-badge">{t}</span>
                      ))}
                      <span className="enrichment-arrow">→</span>
                      <span className="enrichment-value">{rule.add_value}</span>
                    </div>
                    {rule.description && <div className="enrichment-description">{rule.description}</div>}
                    <div className="enrichment-actions">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={rule.is_active}
                          onChange={() => handleSaveEnrichment(
                            { trigger_synonyms: rule.trigger_synonyms, add_value: rule.add_value, apply_to_categories: rule.apply_to_categories, description: rule.description, is_active: !rule.is_active, sort_order: rule.sort_order },
                            rule.id
                          )}
                        />
                        {rule.is_active ? 'Активно' : 'Выключено'}
                      </label>
                      <button className="edit-btn" onClick={() => setEditingEnrichment({ ...rule, trigger_synonyms_text: (rule.trigger_synonyms || []).join(', ') })}>
                        Изменить
                      </button>
                      <button className="delete-btn" onClick={() => handleDeleteEnrichment(rule.id)}>
                        Удалить
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {(editingCategory || showNewCategory) && (
        <CategoryModal
          category={editingCategory || { name: '', synonyms: [], is_active: true, sort_order: categories.length }}
          categories={categories}
          saving={saving}
          onSave={handleSaveCategory}
          onClose={() => { setEditingCategory(null); setShowNewCategory(false); }}
        />
      )}

      {/* Edit Feature Modal */}
      {(editingFeature || showNewFeature) && (
        <FeatureModal
          feature={editingFeature || { name: '', synonyms: [], is_asymmetric: false, use_as_fallback: false, apply_to_categories: [], is_active: false, sort_order: features.length }}
          categories={categories}
          saving={saving}
          onSave={handleSaveFeature}
          onClose={() => { setEditingFeature(null); setShowNewFeature(false); }}
        />
      )}

      {/* Edit Prompt Modal */}
      {editingPrompt && (
        <PromptModal
          prompt={editingPrompt}
          saving={saving}
          onSave={handleSavePrompt}
          onClose={() => setEditingPrompt(null)}
        />
      )}
    </div>
  );
}

// ============================================
// CATEGORY MODAL
// ============================================
function CategoryModal({ category, saving, onSave, onClose }) {
  const [name, setName] = useState(category.name || '');
  const [synonymsText, setSynonymsText] = useState((category.synonyms || []).join(', '));
  const [isActive, setIsActive] = useState(category.is_active !== false);
  const [sortOrder, setSortOrder] = useState(category.sort_order || 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    const synonyms = synonymsText
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    onSave({
      ...category,
      name,
      synonyms,
      is_active: isActive,
      sort_order: parseInt(sortOrder) || 0,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{category.id ? 'Редактировать категорию' : 'Новая категория'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="например: сверло"
              required
            />
          </div>
          <div className="form-group">
            <label>Синонимы (через запятую)</label>
            <textarea
              value={synonymsText}
              onChange={(e) => setSynonymsText(e.target.value)}
              placeholder="сверло, свёрла, сверл, drill"
              rows={3}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Порядок сортировки</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Активна
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// FEATURE MODAL
// ============================================
function FeatureModal({ feature, categories, saving, onSave, onClose }) {
  const [name, setName] = useState(feature.name || '');
  const [synonymsText, setSynonymsText] = useState((feature.synonyms || []).join(', '));
  const [isAsymmetric, setIsAsymmetric] = useState(feature.is_asymmetric || false);
  const [useAsFallback, setUseAsFallback] = useState(feature.use_as_fallback || false);
  const [isSteelGrade, setIsSteelGrade] = useState(feature.is_steel_grade || false);
  const [isThreadType, setIsThreadType] = useState(feature.is_thread_type || false);
  const [applyToAll, setApplyToAll] = useState(
    !feature.apply_to_categories || feature.apply_to_categories.length === 0 || feature.apply_to_categories.includes('all')
  );
  const [selectedCategories, setSelectedCategories] = useState(
    feature.apply_to_categories ? feature.apply_to_categories.filter(c => c !== 'all') : []
  );
  const [description, setDescription] = useState(feature.description || '');
  const [isActive, setIsActive] = useState(feature.is_active !== false);
  const [sortOrder, setSortOrder] = useState(feature.sort_order || 0);

  const handleCategoryToggle = (catName) => {
    setSelectedCategories(prev =>
      prev.includes(catName)
        ? prev.filter(c => c !== catName)
        : [...prev, catName]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const synonyms = synonymsText
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    onSave({
      ...feature,
      name,
      synonyms,
      is_asymmetric: isAsymmetric,
      use_as_fallback: useAsFallback,
      is_steel_grade: isSteelGrade,
      is_thread_type: isThreadType,
      apply_to_categories: applyToAll ? [] : selectedCategories,
      description,
      is_active: isActive,
      sort_order: parseInt(sortOrder) || 0,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content wide">
        <div className="modal-header">
          <h3>{feature.id ? 'Редактировать характеристику' : 'Новая характеристика'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="например: левый"
              required
            />
          </div>
          <div className="form-group">
            <label>Синонимы (через запятую)</label>
            <textarea
              value={synonymsText}
              onChange={(e) => setSynonymsText(e.target.value)}
              placeholder="левый, левая, левое, лев, L"
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание для чего используется"
            />
          </div>
          <div className="form-row">
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isAsymmetric}
                  onChange={(e) => {
                    setIsAsymmetric(e.target.checked);
                    if (e.target.checked) setUseAsFallback(false);
                  }}
                />
                Асимметричная фильтрация
              </label>
              <small>Если включено: заказ БЕЗ этой характеристики совпадает с кандидатами И БЕЗ И С этой характеристикой</small>
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={useAsFallback}
                  onChange={(e) => {
                    setUseAsFallback(e.target.checked);
                    if (e.target.checked) setIsAsymmetric(false);
                  }}
                />
                Выбирать, если нет подходящих товаров без этой характеристики
              </label>
              <small>Сначала останутся подходящие товары без характеристики. Товары с ней будут выбраны, только если других вариантов нет.</small>
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isSteelGrade}
                  onChange={(e) => setIsSteelGrade(e.target.checked)}
                />
                Марка стали
              </label>
              <small>Ищется как подстрока в артикулах номенклатуры (например "ВК8" внутри "PNUM110408ВК8")</small>
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isThreadType}
                  onChange={(e) => setIsThreadType(e.target.checked)}
                />
                Тип резьбы
              </label>
              <small>Фильтр типа резьбы для метчиков/плашек (G, Rc, K, UNC, UNF, TR). Работает как взаимоисключающий фильтр.</small>
            </div>
          </div>
          <div className="form-group">
            <label>Применять к категориям</label>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={applyToAll}
                  onChange={(e) => setApplyToAll(e.target.checked)}
                />
                Ко всем категориям
              </label>
            </div>
            {!applyToAll && (
              <div className="categories-grid">
                {categories.filter(c => c.is_active).map(cat => (
                  <label key={cat.id} className="category-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(cat.name)}
                      onChange={() => handleCategoryToggle(cat.name)}
                    />
                    {cat.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Порядок сортировки</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Активна
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// PROMPT MODAL
// ============================================
function PromptModal({ prompt, saving, onSave, onClose }) {
  const [content, setContent] = useState(prompt.content || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...prompt, content });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content extra-wide">
        <div className="modal-header">
          <h3>Редактировать промпт: {prompt.display_name || prompt.name}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        {prompt.description && (
          <div className="prompt-modal-description">{prompt.description}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Содержимое промпта</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="prompt-textarea"
              required
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// PATTERN RULE CARD
// ============================================
function PatternRuleCard({ rule, categories, saving, onToggle, onSaveCategories }) {
  const [editingCategories, setEditingCategories] = useState(false);
  const [applyToAll, setApplyToAll] = useState(
    !rule.apply_to_categories || rule.apply_to_categories.length === 0 || rule.apply_to_categories.includes('all')
  );
  const [selectedCategories, setSelectedCategories] = useState(
    rule.apply_to_categories ? rule.apply_to_categories.filter(c => c !== 'all') : []
  );

  const patternTypeLabel = {
    diameter: 'Диаметр (ф/Ø + число)',
    thread_metric: 'Метрическая резьба (М + число)',
    bearing_article: 'Артикул подшипника (3–5 цифр)',
    uc_designation: 'UC обозначение (UC/UCP)',
    feed_model: 'Модель / исполнение',
    feed_brand: 'Бренд из названия и свойств',
    feed_properties: 'Запрошенные свойства',
    feed_cable: 'Кабель: марка и размер',
    feed_battery: 'АКБ: В / Ач',
  };

  const handleCategoryToggle = (catName) => {
    setSelectedCategories(prev =>
      prev.includes(catName) ? prev.filter(c => c !== catName) : [...prev, catName]
    );
  };

  const handleSaveCategories = () => {
    const cats = applyToAll ? ['all'] : selectedCategories;
    onSaveCategories(rule, cats);
    setEditingCategories(false);
  };

  return (
    <div className={`item-card ${!rule.is_active ? 'inactive' : ''}`}>
      <div className="item-main">
        <div className="item-name">
          {rule.name}
          <span className="pattern-type-badge">{patternTypeLabel[rule.pattern_type] || rule.pattern_type}</span>
        </div>
        {rule.description && (
          <div className="item-description">{rule.description}</div>
        )}
        <div className="item-categories">
          <strong>Применяется к: </strong>
          {rule.apply_to_categories && rule.apply_to_categories.length > 0 && !rule.apply_to_categories.includes('all')
            ? rule.apply_to_categories.join(', ')
            : 'все категории'}
        </div>
      </div>
      <div className="item-meta">
        <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
          {rule.is_active ? 'Активно' : 'Неактивно'}
        </span>
      </div>
      <div className="item-actions">
        <button
          className={rule.is_active ? 'delete-btn' : 'save-btn'}
          onClick={() => onToggle(rule)}
          disabled={saving}
        >
          {rule.is_active ? 'Выключить' : 'Включить'}
        </button>
        <button className="edit-btn" onClick={() => setEditingCategories(!editingCategories)}>
          Категории
        </button>
      </div>

      {editingCategories && (
        <div className="pattern-rule-categories-editor">
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
              Ко всем категориям
            </label>
          </div>
          {!applyToAll && (
            <div className="categories-grid">
              {categories.filter(c => c.is_active).map(cat => (
                <label key={cat.id} className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.name)}
                    onChange={() => handleCategoryToggle(cat.name)}
                  />
                  {cat.name}
                </label>
              ))}
            </div>
          )}
          <div className="pattern-rule-actions">
            <button className="save-btn" onClick={handleSaveCategories} disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button className="cancel-btn" onClick={() => setEditingCategories(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default FilterSettings;
